import React, { useRef, useState, useEffect } from 'react';
import { Modal } from 'react-bootstrap';
import { connect } from 'react-redux';
import { MiniSpinner } from '../../layouts/Spinner';
import { numberFormat } from '../../../Utils/Common';
import {
    Print,
    goPage,
    sendEmail,
    reSendEmail,
    getPdf,
} from '../../../redux/actions/delivery/delivery.actions';
import { Link } from 'react-router-dom';
import { useReactToPrint } from 'react-to-print';
import { globalDropDown } from '../../../redux/actions/helper/helper.actions';
import DropDownAttachFileGlobal from '../../layouts/DropDownAttachFile.global.js';
import DeliveryCustomTemplate from '../custom_template/DeliveryCustomTemplate';

function DeliveryPrintModal({
    delivery: {
        modal_loading,
        si_data,
        sending_email,
        download_pdf,
        dr_picking,
        new_uploads,
        files_to_remove,
    },
    show,
    onHide,
    Print,
    edit_url,
    auth,
    goPage,
    globalDropDown,
    sendEmail,
    reSendEmail,
    getPdf,
}) {
    const componentRef = useRef();

    // ---- internal notes state ----
    const [showNotes, setShowNotes] = useState(false);
    const [isAdding, setIsAdding] = useState(false);
    const [noteText, setNoteText] = useState('');
    const [notes, setNotes] = useState([]);

    // map si_data.internal_notes → notes[]
    useEffect(() => {
        if (si_data && Array.isArray(si_data.internal_notes)) {
            const mapped = si_data.internal_notes.map((n, idx) => {
                const createdAtRaw =
                    n.created_at && n.created_at.$date
                        ? n.created_at.$date
                        : n.created_at;

                return {
                    id: n._id || `${si_data.dr_id || si_data._id}-${idx}`,
                    message: n.message || '',
                    createdAt: createdAtRaw || null,
                    createdBy: n.created_by || '',
                };
            });

            setNotes(mapped);
            setShowNotes(mapped.length > 0); // auto-open if notes exist
            setIsAdding(false);
        } else {
            setNotes([]);
            setShowNotes(false);
            setIsAdding(false);
        }
    }, [si_data]);

    const status_lbl = (
        <span className={`lbl-status mr-1 lbl-${si_data.status}`}>
            {si_data.status}
        </span>
    );

    // previous button
    const previousBtn = (
        <>
            {si_data.latest_dr_no != si_data.delivery_receipt_no && (
                <div className="mdl-arrow-left arr-link">
                    <Link onClick={() => goPage('prev')}>
                        <i className="la la-angle-left mdl-arrow"></i>
                    </Link>
                </div>
            )}
        </>
    );

    // next button
    const nextBtn = (
        <>
            {si_data.oldest_dr_no != si_data.delivery_receipt_no && (
                <div className="mdl-arrow-right arr-link">
                    <Link onClick={() => goPage('next')}>
                        <i className="la la-angle-right mdl-arrow"></i>
                    </Link>
                </div>
            )}
        </>
    );

    const handlePrint = useReactToPrint({
        content: () => componentRef.current,
        pageStyle: `
            @page { margin: 12mm; }

            /* Scope all print fixes to our template only */
            .dr-print table { border-collapse: collapse !important; }

            /* Remove all header borders except the bottom one */
            .dr-print thead,
            .dr-print thead tr,
            .dr-print thead th {
                border: none !important;
                border-bottom: 1px dotted #000 !important;
            }

            /* Make sure body cells don't bring borders back */
            .dr-print tbody tr,
            .dr-print tbody td,
            .dr-print tbody th {
                border: none !important;
            }

            /* Normalize padding that sometimes changes in print */
            .dr-print th,
            .dr-print td {
                padding: 4px !important;
            }

            /* Optional: keep font + spacing consistent in print */
            .dr-print {
                font-family: "Courier New", Courier, monospace !important;
                font-size: 16px !important;
                line-height: 1.2 !important;
                white-space: pre !important;
                padding: 20px !important;
            }
        `,
    });

    let file_counter =
        (si_data.uploads ? si_data.uploads.length : 0) +
        (new_uploads ? new_uploads.length : 0) -
        (files_to_remove ? files_to_remove.length : 0);

    const toggleNotes = () => {
        setShowNotes((prev) => !prev);
        setIsAdding(false);
    };

    const handleStartAdd = () => {
        setIsAdding(true);
    };

    const handleCancelAdd = () => {
        setIsAdding(false);
        setNoteText('');
    };

    const handleAddNote = () => {
        const trimmed = noteText.trim();
        if (!trimmed) return;

        const createdBy =
            (auth &&
                auth.user &&
                (auth.user.fullname || auth.user.name || auth.user.email)) ||
            si_data.user_prepared_by ||
            'You';

        const newNote = {
            id: `new-${Date.now()}`,
            message: trimmed,
            createdAt: new Date().toISOString(),
            createdBy,
        };

        const updated = [...notes, newNote];
        setNotes(updated);
        setNoteText('');
        setIsAdding(false);

        // TODO: persist internal notes for delivery receipt in backend
    };

    const handleRemoveNote = (id) => {
        const updated = notes.filter((n) => n.id !== id);
        setNotes(updated);
        // TODO: persist removal in backend
    };

    const formatDate = (value) => {
        if (!value) return '';
        try {
            const d = new Date(value);
            if (Number.isNaN(d.getTime())) return '';
            return d.toLocaleDateString();
        } catch {
            return '';
        }
    };

    return (
        <Modal
            centered
            dialogClassName="modal-wdth"
            show={show}
            onHide={onHide}
            size={showNotes ? 'xl' : 'lg'} // grow when notes visible
            animation={true}
        >
            <Modal.Header className="inv-bg-header">
                <div className="col-4 pl-0">
                    <div className="modal-status">{status_lbl}</div>
                </div>
                <div className="col-8 pr-0">
                    <div className="float-right">
                        <Link
                            to={`/deliveries/edit/${si_data.dr_id}`}
                            className="btn btn-circle btn-inv btn-grey mr-1 icon-edit-c"
                        >
                            <i className="ft ft-edit cursor-pointer"></i>
                        </Link>
                        <button
                            onClick={(e) => globalDropDown(e, 'show')}
                            className="btn btn-circle btn-inv btn-grey mr-1 icon-paper-c relative"
                        >
                            <i className="ft ft-paperclip cursor-pointer"></i>
                            {file_counter > 0 ? (
                                <span className="badge badge-pill badge-sm badge-danger badge-up badge-file">
                                    {file_counter > 9 ? '9+' : file_counter}
                                </span>
                            ) : (
                                <></>
                            )}
                        </button>
                        <DropDownAttachFileGlobal
                            origin="delivery"
                            origin_data={si_data}
                            new_uploads={new_uploads}
                            files_to_remove={files_to_remove}
                        />
                        {si_data.email_sent ? (
                            <button
                                className="btn btn-circle btn-inv btn-grey mr-1"
                                onClick={(e) => reSendEmail(e, si_data.dr_id)}
                            >
                                <i
                                    className={
                                        sending_email
                                            ? 'icon-loading-custom icon-20 cursor-pointer'
                                            : 'ft-custom-mail-check cursor-pointer'
                                    }
                                ></i>
                            </button>
                        ) : (
                            <button
                                className="btn btn-circle btn-inv btn-grey mr-1"
                                onClick={(e) => sendEmail(e, si_data.dr_id)}
                            >
                                <i
                                    className={
                                        sending_email
                                            ? 'icon-loading-custom icon-20 cursor-pointer'
                                            : 'ft-custom-mail-x cursor-pointer'
                                    }
                                ></i>
                            </button>
                        )}
                        <button
                            className="btn btn-circle btn-inv btn-grey mr-1 icon-pdf-c"
                            onClick={(e) =>
                                getPdf(
                                    e,
                                    si_data.dr_id,
                                    si_data.delivery_receipt_no,
                                    si_data.customer.name,
                                    dr_picking
                                )
                            }
                        >
                            <i
                                className={
                                    download_pdf
                                        ? 'icon-loading-custom icon-20 cursor-pointer'
                                        : 'la la-file-pdf-o cursor-pointer'
                                }
                            ></i>
                        </button>
                        <button
                            onClick={handlePrint}
                            className="btn btn-circle btn-inv btn-grey mr-1"
                        >
                            <i className="ft ft-printer cursor-pointer"></i>
                        </button>

                        {/* manual toggle – drawer auto-opens if internal_notes exist */}
                        <button
                            onClick={toggleNotes}
                            className="btn btn-circle btn-inv btn-grey mr-1"
                        >
                            <i className="ft ft-message-square cursor-pointer"></i>
                        </button>

                        <button
                            onClick={onHide}
                            className="btn btn-circle btn-inv m-close-btn cursor-pointer"
                        >
                            <i className="ft ft-x cursor-pointer"></i>
                        </button>
                    </div>
                </div>
            </Modal.Header>
            <Modal.Body className="unset-scroll p-0 m-0">
                {/* main + internal notes inside one border */}
                <div className="sd-border modal-container">
                    <div className="modal-main">
                        {previousBtn}
                        {modal_loading ? (
                            <MiniSpinner />
                        ) : (
                            <DeliveryCustomTemplate
                                componentRef={componentRef}
                            />
                        )}
                        {nextBtn}
                    </div>

                    {showNotes && (
                        <div className="internal-notes-panel">
                            <div className="internal-notes-header">
                                <span>Internal Notes</span>
                                <button
                                    type="button"
                                    className="btn btn-sm btn-light"
                                    onClick={toggleNotes}
                                >
                                    <i className="ft ft-x" />
                                </button>
                            </div>

                            <div className="internal-notes-content">
                                {isAdding && (
                                    <div className="internal-notes-form mb-3">
                                        <textarea
                                            className="form-control mb-2"
                                            rows="3"
                                            value={noteText}
                                            onChange={(e) =>
                                                setNoteText(e.target.value)
                                            }
                                            placeholder="Type internal note here..."
                                        />
                                        <div className="d-flex justify-content-end">
                                            <button
                                                type="button"
                                                className="btn btn-sm btn-secondary mr-2"
                                                onClick={handleCancelAdd}
                                            >
                                                Cancel
                                            </button>
                                            <button
                                                type="button"
                                                className="btn btn-sm btn-primary"
                                                onClick={handleAddNote}
                                            >
                                                Save
                                            </button>
                                        </div>
                                    </div>
                                )}

                                {notes.length === 0 && !isAdding && (
                                    <p className="text-muted f-12">
                                        No internal notes yet.
                                    </p>
                                )}

                                {notes.map((note) => (
                                    <div
                                        key={note.id}
                                        className="note-card mb-3"
                                    >
                                        <div className="note-card-header">
                                            <div className="note-user-icon">
                                                <i className="ft ft-user" />
                                            </div>
                                            <div className="note-user-meta">
                                                <div className="note-user-name">
                                                    {note.createdBy || '—'}
                                                </div>
                                                <div className="note-date">
                                                    {formatDate(note.createdAt)}
                                                </div>
                                            </div>
                                            <button
                                                type="button"
                                                className="btn btn-link btn-sm p-0 ml-auto"
                                                onClick={() =>
                                                    handleRemoveNote(note.id)
                                                }
                                            >
                                                <i className="ft ft-trash-2" />
                                            </button>
                                        </div>
                                        <div className="note-body">
                                            {note.message}
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* {!isAdding && (
                                <button
                                    type="button"
                                    className="internal-notes-plus-btn"
                                    onClick={handleStartAdd}
                                >
                                    <span>+</span>
                                </button>
                            )} */}
                        </div>
                    )}
                </div>
            </Modal.Body>
        </Modal>
    );
}

const mapStateToProps = (state) => ({
    delivery: state.delivery,
    auth: state.auth,
});

export default connect(mapStateToProps, {
    Print,
    goPage,
    globalDropDown,
    sendEmail,
    reSendEmail,
    getPdf,
})(DeliveryPrintModal);
